/**
Complex math library.  Started by Collaborative Consulting and ADI in 2012.  Heavily modified by Matt Duff in 2019.


This library has three sections:
1) Basic math: 
Includes plus, mult, divide and their Destroy versions.  The "Destroy" versions reuse the input variable to
avoid creating a new object and are therefore considerably faster.  However they destroy the input variable, so have
to be used quite carefully.  
Our basic math functions are used at the lowest level of our simulations, and are run thousands of times.  
It is critical that these functions run quickly.  In 2019, we compared our add, multiply and divide to to the open source complex library, Complex.js.  This library came out after
we had created this homemade one.  Performance of this homemade library compared favorably to that library.  See ARNA-3487 for more details.

*/

/**
@author Created by : Archita Ghosh on behalf of Collaborative Consulting and ADI.
@function Complex 
@param {int} real part of the complex number which is in the form of a+ib.
@param {int} imaginary part of the complex number.
@returns A complex number in the form of a+ib.
@description This function creates a complex number.
The parameters to this function are real part and the imaginary part.
*/
function Complex(real, imaginary) {
  this.real = real;
  this.imaginary = imaginary;
}

//Does Filter Wizard use this???
/**
@description  The constants converted into complex numbers which are used in the algorithm
*/
/*
Complex["0"] = new Complex(0, 0);
Complex["1"] = new Complex(1, 0);
Complex["I"] = new Complex(0, 1, 1, Math.PI / 2);
Complex["PI"] = new Complex(Math.PI, 0, Math.PI, 0);
Complex["E"] = new Complex(Math.E, 0, Math.E, 0);
*/

/*******************************************   Basic Math Functions ******************************************************/

/**
@function plus
@returns summation of two complex numbers.
@description  This function takes as a parameter another complex number and returns a sum of two complex number
@example  Let the first complex number be x+iy and second complex number be a+ib.
The result of (x+iy).plus(a+ib) = (x+a) +i(y+b)
*/

Complex.prototype.plus = function (c) {
  return new Complex(this.real + c.real, this.imaginary + c.imaginary);
};

/*This addition function is much faster than the regular plus function, because it reuses the incoming variable instead of creating a new return variable.
However it destroys the original value in the variable in the process, so this function should be used with care.*/
Complex.prototype.plusDestroy = function (c) {
  var realPart = c.real;
  var imagPart = c.imaginary;
  c.real = this.real + realPart;
  c.imaginary = this.imaginary + imagPart;
  return c;
};

/**
@function subs
@returns substraction of two complex numbers.
@description  This function takes as a parameter another complex number and returns a substraction of two complex number
@example  Let the first complex number be x+iy and second complex number be a+ib.
The result of (x+iy).subs(a+ib) = (x-a) +i(y-b)
*/

Complex.prototype.subs = function (c) {
  return new Complex(this.real - c.real, this.imaginary - c.imaginary);
};

/*This addition function is much faster than the regular subs function, because it reuses the incoming variable instead of creating a new return variable.
However it destroys the original value in the variable in the process, so this function should be used with care.*/
Complex.prototype.subsDestroy = function (c) {
  var realPart = c.real;
  var imagPart = c.imaginary;
  c.real = this.real - realPart;
  c.imaginary = this.imaginary - imagPart;
  return c;
};

/**
@function mult
@returns multiplication of two complex numbers.
@description  This function takes as a parameter another complex number and returns a multiplication of two complex number
@example  Let the first complex number be x+iy and second complex number be a+ib.
The result of (x+iy).mult(a+ib) = (x*a)-(y*b) + i(y*a)+(b*x)
*/

Complex.prototype.mult = function (c) {
  var realPart = c.real;
  var imagPart = c.imaginary;
  return new Complex(
    realPart * this.real - imagPart * this.imaginary,
    this.imaginary * realPart + imagPart * this.real
  );
};

/*This multiply function is much faster than the regular mult function, because it reuses the incoming variable instead of creating a new return variable.
However it destroys the value of the original variable in the process, so this function should be used with care.*/
Complex.prototype.multDestroy = function (c) {
  var realPart = c.real;
  var imagPart = c.imaginary;
  c.real = realPart * this.real - imagPart * this.imaginary;
  c.imaginary = this.imaginary * realPart + imagPart * this.real;

  return c;
};

/**
@function divide
@returns division of two complex numbers.
@description  This function takes as a parameter another complex number and returns a division of two complex number
In April 2019 we updated this version to use one divide instead of 2, giving it a speed boost. See ARNA-3487 for details.
 */
Complex.prototype.divide = function (c) {
  //multiply top and bottom by complex conjugate
  var numer_real = c.real * this.real + this.imaginary * c.imaginary;
  var numer_imag = c.real * this.imaginary - c.imaginary * this.real;
  var denom_real = c.real * c.real + c.imaginary * c.imaginary;
  if (denom_real === 0) {
    return new Complex(Infinity, 0);
  }
  if (denom_real === Infinity) {
    return new Complex(0, 0);
  }
  var denom_real_invert = 1 / denom_real;
  return new Complex(numer_real * denom_real_invert, numer_imag * denom_real_invert);
};

/**
@function divideDestroy
@returns division of two complex numbers.
@description  This function takes as a parameter another complex number and returns a division of two complex number. 
It reuses the input variable.  Because it does not have to create a new object, it's considerably faster than the
regular divide function, but also more dangerous, as the input variable is destroyed.

In April 2019 we updated this version to use one divide instead of 2, giving it a speed boost. See ARNA-3487 for details.
 */
Complex.prototype.divideDestroy = function (c) {
  //multiply top and bottom by complex conjugate
  var numer_real = c.real * this.real + this.imaginary * c.imaginary;
  var numer_imag = c.real * this.imaginary - c.imaginary * this.real;
  var denom_real = c.real * c.real + c.imaginary * c.imaginary;
  if (denom_real === 0) {
    c.real = Infinity;
    c.imaginary = 0;
    return c;
  }
  if (denom_real === Infinity) {
    c.real = 0;
    c.imaginary = 0;
    return c;
  }
  var denom_real_invert = 1 / denom_real;
  c.real = numer_real * denom_real_invert;
  c.imaginary = numer_imag * denom_real_invert;
  return c;
};

/**
@function invert
@returns multiplicative inverse of input
@description  Inverts a complex number (1/x).  
 */
Complex.prototype.reciprocal = function () {
  let denom_real = this.real * this.real + this.imaginary * this.imaginary;
  if (denom_real === 0) {
    return new Complex(Infinity, 0);
  }
  if (denom_real === Infinity) {
    return new Complex(0, 0);
  }
  var denominator_inverted = 1 / denom_real;
  return new Complex(this.real * denominator_inverted, -1 * this.imaginary * denominator_inverted);
};

/**
@function invertDestroy
@returns multiplicative inverse of input
@description  Inverts a complex number (1/x).  Because it does not have to create a new object, it's considerably faster than the
regular invert function, but also more dangerous, as the input variable is destroyed.

Complex.prototype.invertDestroy = function () {
    var denominator_inverted = 1/(this.real * this.real + this.imaginary * this.imaginary);
    this.real = this.real * denominator_inverted;
    this.imaginary = -1 * this.imaginary * denominator_inverted;
    return this;
}
/*


/**
@function negate
@returns multiplicative inverse of input
@description  Inverts a complex number (1/x).  
 */
Complex.prototype.negate = function () {
  return new Complex(this.real * -1, this.imaginary * -1);
};

/**
@function negateDestroy
@returns multiplicative inverse of input
@description  Inverts a complex number (1/x).  Because it does not have to create a new object, it's considerably faster than the
regular invert function, but also more dangerous, as the input variable is destroyed.
 */
Complex.prototype.negateDestroy = function () {
  this.real = this.real * -1;
  this.imaginary = this.imaginary * -1;
  return this;
};

/**
@function parallel
@returns parallel combination of two complex numbers
@description parallel combination of two complex numbers
 */
Complex.prototype.parallel = function (z2) {
  let z1 = this;
  let numerator = z1.mult(z2);
  let denominator = z1.plus(z2);
  return numerator.divideDestroy(denominator);
};

/**
@function magnitude
@returns magnitude of a complex number
@description  This function calculates and returns the magnitude of the complex number.
@example  Let the complex number be x+iy 
The result of (x+iy).magnitude() = sqrt(x*x +y*y)
*/
Complex.prototype.magnitude = function () {
  return Math.sqrt(this.real * this.real + this.imaginary * this.imaginary);
};

/**
@function phaseRadians
@returns phase of complex number in radians
@description  This function calculates and returns the phase of the complex number in radians.
*/
Complex.prototype.phaseRadians = function () {
  return Math.atan2(this.imaginary, this.real);
};

/**
@function phaseRadians
@returns phase of complex number in degrees
@description  This function calculates and returns the phase of the complex number in degrees.
*/
Complex.prototype.phaseDegrees = function () {
  let phaseRadians = this.phaseRadians();
  return phaseRadians * (180 / Math.PI);
};

/**
@function arctan
@param a complex number.
@returns inverse tangent of a complex number
@description  This function takes as a parameter another complex number and returns an inverse tangent of a complex number
*/
// Complex.prototype.arctan = function (c) {
//     var i = Complex.I;
//     return i.mult(Complex.log(i.plus(c).divide(i.subs(c)))).divide(new Complex(2, 0, 2, 0));
// }

/**
@function floor
@param a complex number.
@returns floor value of a complex number.
@description  This function takes as a parameter another complex number and returns a floor value of a complex number.
*/
// Complex.prototype.floor = function (c) {
//     return new Complex(Math.floor(c.real), Math.floor(c.imaginary));
// }

/**
@function log
@param a complex number.
@returns log of a  complex numbers.
@description  This function takes as a parameter another complex number and returns a log of the complex number
*/
// Complex.prototype.log = function (c) {
//     return new Complex(Math.log(c.real), c.t + 2 * Math.PI * Math.floor(-c.t / (2 * Math.PI) + 1 / 2));
// }

export function createComplex(real, imaginary) {
  return new Complex(real, imaginary);
}

//Make scalar value complex.  Handy for use with optional values, where you aren't sure if they are undefined
export function complexify(scalarValue) {
  if (scalarValue != undefined) {
    return new Complex(scalarValue, 0);
  } else {
    return undefined;
  }
}

/**
@function complexNumberRaisedToPower
@param complex number, integer power
@returns complexNumber
*/
export function complexNumberRaisedToPower(c, n) {
  var modC, theta, retComplex, realPart, imaginaryPart, modCpowern;

  // |x+iy| = Math.sqrt(x^2 + y^2) = |z|
  modC = Math.sqrt(c.real * c.real + c.imaginary * c.imaginary);

  // |x+iy|^n  = |z|^n
  modCpowern = Math.pow(modC, n);

  //theta=tan^(-1)(y/x)>
  theta = Math.atan2(c.imaginary, c.real);

  //|z|^n * cos(n* theta)
  realPart = modCpowern * Math.cos(n * theta);

  //|z|^n * sin(n* theta)
  imaginaryPart = modCpowern * Math.sin(n * theta);

  //zn = rn(cos(ntheta)+i sin(ntheta))
  retComplex = new Complex(realPart, imaginaryPart);

  return retComplex;
}

//Returns the transfer function of a divider.  Z1 is on top.  Z2 is on bottom.
export function divider(z1, z2) {
  let denominator = createComplex(z1.real + z2.real, z1.imaginary + z2.imaginary);
  let divider = z2.divideDestroy(denominator);
  return divider;
}

//Given an array of scalar numbers, convert into an array of complex numbers
export function arrayCreate(scalarArray) {
  let complexArray = [];
  for (let i = 0; i < scalarArray.length; i++) {
    complexArray[i] = new Complex(scalarArray[i], 0);
  }
  return complexArray;
}

//Given an array of complex numbers, returns a scalar array of the magnitudes in V/V
export function arrayMagnitude(complexArray) {
  let scalarArray = [];
  for (let i = 0; i < complexArray.length; i++) {
    scalarArray[i] = complexArray[i].magnitude();
  }
  return scalarArray;
}

//Given an array of complex numbers, returns a scalar array of the phases in degrees
export function arrayPhaseDegrees(complexArray) {
  let phaseRadians = arrayPhaseRadians(complexArray);
  let phaseDegrees = [];
  for (let i = 0; i < phaseRadians.length; i++) {
    phaseDegrees[i] = phaseRadians[i] * (180 / Math.PI);
  }
  return phaseDegrees;
}

//Given an array of complex numbers, returns a scalar array of the phases in radians
export function arrayPhaseRadians(complexArray) {
  let wrappedPhase = [];
  for (let i = 0; i < complexArray.length; i++) {
    wrappedPhase[i] = complexArray[i].phaseRadians();
  }
  let unwrappedPhase = unwrap(wrappedPhase);
  return unwrappedPhase;
}

//Given an two arrays of complex numbers of the same length, multiply each element in first array by corresponding
//element in the second array
export function arrayMultiply(complexArray1, complexArray2) {
  let product = [];
  for (let i = 0; i < complexArray1.length; i++) {
    product[i] = complexArray1[i].mult(complexArray2[i]);
  }
  return product;
}

//Given an two arrays of complex numbers of the same length, create impedance divider with each element in first array
//and corresponding element in the second array
export function arrayDivider(complexArray1, complexArray2) {
  let divider = [];
  for (let i = 0; i < complexArray1.length; i++) {
    divider[i] = this.divider(complexArray1[i], complexArray2[i]);
  }
  return divider;
}

//Unwraps phase array so that phase can continue to grow rather than being constrained between -PI and PI radians
//For example.  The following sequence:
// 2PI/3, -2PI/3, 0, 2PI/3
// will be turned into
// 2PI/3, 4PI/3, 6PI/3, 8PI/3
function unwrap(phaseArray) {
  var halfcircle = Math.PI,
    fullcircle = 2 * Math.PI;

  var length = phaseArray.length;
  for (var n = 1; n <= length - 1; n++) {
    var phase_diff = phaseArray[n] - phaseArray[n - 1];
    if (phase_diff > halfcircle) {
      for (var k = n; k <= length - 1; k++) {
        phaseArray[k] = phaseArray[k] - fullcircle;
      }
    }
    if (phase_diff < -halfcircle) {
      for (var j = n; j <= length - 1; j++) {
        phaseArray[j] = phaseArray[j] + fullcircle;
      }
    }
  }

  return phaseArray;
}
